import { normalize } from '@/lib/normalize.js'
import schema, { pluralize, singularize } from '@/schema.js'
import { authenticationComplete } from '@/lib/authentication.js'
import { currentAuthRef } from '@/lib/authentication.js'
import eventbus from '@/eventbus.js'

// Remove a normalized object locally and clear relationships through
// prototype setters so known inverse links do not keep dangling IDs.
function removeEntityFromStore(state, entity, objectOrId) {
  const idAttr = entity.idAttr || 'id';
  const id = (objectOrId && typeof objectOrId === 'object') ?
    objectOrId[idAttr] :
    objectOrId;
  const bucket = state[entity.key];
  const obj = bucket?.[id];
  if (!obj) return;

  Object.keys(entity.relationships || {}).forEach(rel => {
    const relationship = entity.relationships[rel];
    obj[rel] = relationship.repeat ? [] : null;
  });

  delete bucket[id];
}

export class SharedStore {
  constructor(state) {
    this.state = state;
    this.computed = {};
    this.getters = new Proxy(this.computed, {
      get(target, prop, receiver) {
        return Reflect.get(target, prop, receiver).value;
      },
      set(target, prop, value, receiver) {
        if (!(prop in target)) return false;
        Reflect.get(target, prop, receiver).value = value;
        return true;
      }
    });
  }

  setLoadingStatus(entityName, isLoading) {
    if (isLoading) {
      if (this.state.loadingEntities.indexOf(entityName) < 0) {
        this.state.loadingEntities.push(entityName);
      }
      return;
    }

    let i;
    while ((i = this.state.loadingEntities.indexOf(entityName)) >= 0) {
      this.state.loadingEntities.splice(i, 1);
    }
    eventbus.emit('loadDidFinish', entityName);
  }

  didLoadEntities() {}

  async loadEntities({ url, entity, parent } = {}) {
    console.debug(`loadEntities ${url}`);
    if (!entity) {
      console.warn('Attempted to load with null entity', url);
      return;
    }

    const entitySchema = Array.isArray(entity) ? entity[0] : entity;
    const entityName = entitySchema.key;
    const existingPromise = this.state.loading[url];
    if (existingPromise) {
      console.log('store: Already loading %s; skipping', entityName);
      return existingPromise;
    }

    this.setLoadingStatus(entityName, true);

    const promise = new Promise(async (resolve, reject) => {
      try {
        console.debug(`Loading "${entityName}" (${url})`);

        await authenticationComplete();
        console.debug(`Continue load for "${entityName}" (${url})`);

        const response = await this.axios.get(url);
        if (!response) {
          reject();
          return;
        }

        if (!this.state[entityName]) {
          this.state[entityName] = Object.create(null);
        }

        normalize(response.data, this.state, entity);

        if (parent && Array.isArray(entity) && Array.isArray(response.data) && response.data.length < 1) {
          parent[pluralize(entityName) + 'Ids'] = [];
        }

        const result = {
          entity: entitySchema,
          entityName,
          response,
        };
        this.didLoadEntities(result);
        resolve(result);
      }
      catch (err) {
        const code = err.code ?? err.response?.status;
        console.error(`Load error ${code} for ${entityName}`, err);
        if (code === 401) {
          console.error("didn't have auth");
        }
        reject(err);
      }
      finally {
        delete this.state.loading[url];
        this.setLoadingStatus(entityName, false);
      }
    });

    this.state.loading[url] = promise;
    return promise;
  }

  // Checks payload for NEW-xxx IDs and moves them to ephemeralKey for
  // the server round trip. This connects the ID that the server assigns
  // back to the object that we just sent, which only had an ephemeralID
  //
  prepareEphemeralObjectForSave(entity, object, sourceObject = object) {
    const moveEphemeralForObject = (obj, sourceObj, objEntity) => {
      if (!obj || typeof obj !== 'object') return;

      const idAttr = objEntity.idAttr || 'id';
      const bucket = this.state[objEntity.key] || (this.state[objEntity.key] = Object.create(null));

      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value == null) return;
        const sourceValue = sourceObj?.[key];
        const relationship = objEntity.relationships?.[key];
        const valueEntity = relationship?.entity || objEntity;

        if (key === idAttr && typeof value === 'string' && value.startsWith('NEW-')) {
          bucket[value] = sourceObj && typeof sourceObj === 'object' ? sourceObj : obj;
          obj.ephemeralKey = value;
          delete obj[idAttr];
          return;
        }

        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            value.forEach((child, index) => moveEphemeralForObject(child, sourceValue?.[index], valueEntity));
          }
          else {
            moveEphemeralForObject(value, sourceValue, valueEntity);
          }
        }
      });
    };

    moveEphemeralForObject(object, sourceObject, entity);
  }

  // If the save resulted in object creation, something
  // in the response will have an ephemeralKey attribute.
  // This connects that back to the NEW-xxx key we sent
  // to maintain the relationship graph in the store.
  adoptEphemeralSaveResult(entity, responseData) {
    const idAttr = entity.idAttr || 'id';
    const finalId = responseData?.[idAttr];
    const ephemeralKey = responseData?.ephemeralKey;
    if (!finalId || !ephemeralKey) return;

    const bucket = this.state[entity.key];
    const ephemeralObj = bucket?.[ephemeralKey];
    if (!ephemeralObj) return;

    bucket[finalId] = ephemeralObj;
    delete bucket[ephemeralKey];
    ephemeralObj[idAttr] = finalId;
    delete ephemeralObj.ephemeralKey;
  }

  async saveEntity({ path, entity, object, sourceObject = object, params } = {}) {
    if (!entity) {
      console.error(`Entity is undefined for ${path}`);
      return;
    }

    let payload = {...params};
    let requestObject = object;
    let create = false;
    const idAttr = entity.idAttr || 'id';

    if (requestObject) {
      this.prepareEphemeralObjectForSave(entity, requestObject, sourceObject);
      payload[singularize(entity.key)] = requestObject;
    }

    console.debug('store: saveEntity', entity.key, path);

    let response;
    if (requestObject?.[idAttr]) {
      response = await this.axios.put(`${path}/${requestObject[idAttr]}`, payload);
    }
    else {
      create = true;
      response = await this.axios.post(path, payload);
    }

    if (response?.data) {
      const savedObject = response.data;
      this.adoptEphemeralSaveResult(entity, savedObject);

      // Once the persisted ID is known, the ephemeral handshake is complete.
      // Keep that key out of the normalized store object so the client only
      // retains the database ID after create.
      if (savedObject && typeof savedObject === 'object') {
        delete savedObject.ephemeralKey;
      }

      normalize(savedObject, this.state, entity);
    }

    if (response) {
      response.isCreateResponse = create;
    }

    return response;
  }

  // Delete a persisted entity and then clear its normalized store entry so
  // prototype relationship setters can clean up any known inverse links.
  async destroyEntity({ path, entity, object, id } = {}) {
    if (!entity) {
      console.error(`Entity is undefined for ${path}`);
      return;
    }

    const idAttr = entity.idAttr || 'id';
    const entityId = id ?? object?.[idAttr];
    if (entityId == null) {
      console.warn('Attempted to destroy entity without an id', entity.key, object);
      return;
    }

    if (typeof entityId === 'string' && entityId.startsWith('NEW-')) {
      removeEntityFromStore(this.state, entity, entityId);
      return { data: object, isDestroyResponse: true };
    }

    const deletePath = path.endsWith(`/${entityId}`) ? path : `${path}/${entityId}`;
    const response = await this.axios.delete(deletePath);
    removeEntityFromStore(this.state, entity, entityId);

    if (response) {
      response.isDestroyResponse = true;
    }

    return response;
  }

  setChannelConnected(channelName, isConnected) {
    console.log(`${isConnected ? 'connected': 'disconnected'} Cable: ${channelName}`);
    this.state.channelConnected[channelName] = isConnected;
  }

  async handleUpdate(payload) {
    console.debug("Update payload", payload);
    if (!payload || typeof payload !== 'object') return;

    for (const entityName of Object.keys(payload)) {
      const bucketPayload = payload[entityName];
      const singularName = singularize(entityName);
      const entity = schema[singularName];
      if (!entity) {
        console.warn(`handleUpdate: unknown entity '${entityName}'`);
        continue;
      }

      if (!this.state[singularName]) {
        this.state[singularName] = Object.create(null);
      }

      // Cable payloads still tend to use the old plural entity keys
      // (`eventSources`, `participants`, etc). Normalize against the
      // singular shared-store bucket so updates land in state.eventSource,
      // state.participant, and the other new normalized stores.
      //
      // This seems like it needs refactoring
      //
      if (Array.isArray(bucketPayload)) {
        normalize(bucketPayload, this.state, [entity]);
      }
      else if (bucketPayload && typeof bucketPayload === 'object') {
        const ids = Object.keys(bucketPayload);
        const rows = new Array(ids.length);
        for (let i = 0; i < ids.length; i++) {
          const row = bucketPayload[ids[i]];
          if (row && typeof row === 'object' && row[entity.idAttr] == null) {
            rows[i] = { ...row, [entity.idAttr]: ids[i] };
          }
          else {
            rows[i] = row;
          }
        }
        normalize(rows, this.state, [entity]);
      }
    }
  }

  fetchToken(un, pw) {
    return this.axios.request({
      method: 'post',
      url: 'authenticate',
      headers: {
        'Accept': 'application/json',
        'Content-type': 'application/json',
      },
      data: {
        u: un,
        p: pw,
      }
    })
    .then(r=> {
      console.log("Login Response:", r);
      let data = r.data;

      currentAuthRef.value = data.token;
    });
  }

  setFilteredRaceIds(filteredRaceIds = {}) {
    this.state.filteredRaceIds = filteredRaceIds;
  }
}
